// main logic
import UIHandler from './UIHandler.js';
// filters
import FilterHandler from './FilterHandler.js';

// V2 UI modules
import UIBridge from './ui/UIBridge.js';
import SliderControl from './ui/SliderControl.js';
import ToggleControl from './ui/ToggleControl.js';

import PropertyCardManager from './ui/PropertyCardManager.js';
import SummaryPanel from './ui/SummaryPanel.js';
import OrderManager from './ui/OrderManager.js';
import GlassChipSelector from './ui/GlassChipSelector.js';
import AnimationHelper from './ui/AnimationHelper.js';
import InfoTooltip from './ui/InfoTooltip.js';
import SegmentedControl from './ui/SegmentedControl.js';
import FaqModal from './ui/FaqModal.js';
import HeroThicknessModal from './ui/HeroThicknessModal.js';
import AuthManager from './ui/AuthManager.js';
import DashboardManager from './ui/DashboardManager.js';
import PricingEngine from './PricingEngine.js';
import WizardManager from './ui/WizardManager.js';
import FirebaseService from './services/FirebaseService.js';
import EmailService from './services/EmailService.js';
import PDFExporter from './services/PDFExporter.js';

function showToast(message, type = 'info') {
  const toast = document.createElement('div');
  toast.className = `cdr-toast cdr-toast-${type}`;
  toast.textContent = message;
  document.body.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add('visible'));
  setTimeout(() => {
    toast.classList.remove('visible');
    setTimeout(() => toast.remove(), 300);
  }, 3500);
}

function onReady() {

  // Create wizard immediately — no waiting for data
  const wizard = new WizardManager();
  window._cdrWizard = wizard;
  if (!localStorage.getItem('cdr-wizard-v2')) {
    wizard.show();
  }

  let filterHandler = new FilterHandler();

  let uiHandler = new UIHandler(filterHandler, window.ViewApi,
    ()=>{
      console.log('Data ready');
      filterHandler.setup();

      // Expose for SummaryPanel weight/spacer access
      window._cdrStructure = uiHandler.structure;
      window._cdrFilterHandler = filterHandler;

      // Initialize pricing engine from API data
      const pricingEngine = new PricingEngine();
      if (window._cdrPricing && window._cdrVolumePriceTables) {
        pricingEngine.init(window._cdrPricing, window._cdrVolumePriceTables);
      }
      window._cdrPricingEngine = pricingEngine;

      // --- V2 UI initialization ---
      initV2UI(wizard);
  });

};

function initV2UI(wizard) {
  const bridge = new UIBridge();

  // Connect wizard to bridge now that data is ready
  if (wizard) wizard.setBridge(bridge);
  const cardManager = new PropertyCardManager();

  // 1. Manufacturer — segmented chip select (two rows)
  new SegmentedControl(bridge, 'manufacturer', '.property-card[data-filter="manufacturer"]', {
    values: ['Alle', 'Glas Trösch', 'AGC', 'Hero-Fire', 'Pilkington'],
    nativeSelector: '.manufacturer'
  });

  // 2. Fire resistance class — segmented control
  new SegmentedControl(bridge, 'class', '.property-card[data-filter="class"]', {
    values: ['EI30', 'EI60', 'EI90'],
    nativeSelector: '.class'
  });

  // 3. Insulation (U-Wert) — 12-step slider
  new SliderControl(bridge, 'insulation', '.property-card[data-filter="insulation"]', {
    values: [
      'U-Wert 0.5', 'U-Wert 0.6', 'U-Wert 0.7', 'U-Wert 0.8',
      'U-Wert 0.9', 'U-Wert 1.0', 'U-Wert 1.1', 'U-Wert 1.2',
      'U-Wert 1.3', 'U-Wert 1.4', 'U-Wert 1.7', 'U-Wert 5.4'
    ],
    labels: [
      '0.5', '0.6', '0.7', '0.8',
      '0.9', '1.0', '1.1', '1.2',
      '1.3', '1.4', '1.7', '5.4'
    ]
  });

  // 4. Structure — still registered for cascade (UI card hidden, merged into U-Wert)
  new SegmentedControl(bridge, 'structure', '.property-card[data-filter="structure"]', {
    values: ['1-glazed (mono)', '2-glazed-ISO', '3-glazed-ISO'],
    nativeSelector: '.structure'
  });

  // U-Wert/Struktur zone label highlighting + thumb value
  const insulationSlider = document.getElementById('insulation-slider');
  const zoneLabels = document.querySelectorAll('#slider-zones .zone-label');
  const thumbValue = document.getElementById('slider-thumb-value');
  const uWertLabels = ['0.5','0.6','0.7','0.8','0.9','1.0','1.1','1.2','1.3','1.4','1.7','5.4'];

  function updateThumbPosition(slider) {
    if (!thumbValue || !slider) return;
    const min = parseFloat(slider.min);
    const max = parseFloat(slider.max);
    const val = parseFloat(slider.value);
    const pct = (val - min) / (max - min);
    // Account for thumb width (17px each side) within slider track
    const thumbHalf = 17;
    const sliderWidth = slider.offsetWidth;
    const trackWidth = sliderWidth - thumbHalf * 2;
    const px = thumbHalf + pct * trackWidth;
    thumbValue.style.left = px + 'px';
    thumbValue.textContent = uWertLabels[val] || val;
  }

  function updateZoneHighlight(idx) {
    if (!zoneLabels.length) return;
    // 0-4 → 3x ISO, 5-10 → 2x ISO, 11 → Mono
    zoneLabels.forEach(z => z.classList.remove('active'));
    if (idx <= 4) zoneLabels[0].classList.add('active');       // 3x ISO
    else if (idx <= 10) zoneLabels[1].classList.add('active'); // 2x ISO
    else zoneLabels[2].classList.add('active');                 // Mono
  }
  if (insulationSlider) {
    insulationSlider.addEventListener('input', () => {
      updateZoneHighlight(parseInt(insulationSlider.value));
      updateThumbPosition(insulationSlider);
    });
    updateZoneHighlight(parseInt(insulationSlider.value));
    updateThumbPosition(insulationSlider);
    // Reposition on resize
    window.addEventListener('resize', () => updateThumbPosition(insulationSlider));
  }

  // 5. Application — segmented control with icons
  new SegmentedControl(bridge, 'application', '.property-card[data-filter="application"]', {
    values: ['Inside', 'Inside/Outside', 'Outside/Outside'],
    nativeSelector: '.application'
  });

  // 6. Sound insulation — display only (synced from glass data)
  initSoundCardSync();

  // 7. Resistance — 4-option segmented control
  new SegmentedControl(bridge, 'resistance', '.property-card[data-filter="resistance"]', {
    values: ['-', 'RC 2 (WK 2) / P4A', 'RC 3 (WK 3) / P5A', 'RC 4 (WK 4) / P6B'],
    nativeSelector: '.resistance'
  });

  // 8. Toggle block: White Glass, Butt Joints, Absturzhemmung
  new ToggleControl(bridge, 'white-glass', '#white-glass-toggle');
  new ToggleControl(bridge, 'butt-joints', '#butt-joints-toggle');
  new ToggleControl(bridge, 'absturzhemmung', '#absturzhemmung-toggle');

  // Summary panel (right column live updates)
  new SummaryPanel(bridge);

  // Order manager (shopping cart + localStorage)
  new OrderManager(bridge);

  // Glass chip selector (center column)
  new GlassChipSelector();

  // Info tooltips
  new InfoTooltip();

  // FAQ modal
  new FaqModal();

  // Reset glass button — resets all filters to defaults + V2 UI state
  const resetBtn = document.getElementById('reset-glass-btn');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      const fh = window._cdrFilterHandler;
      if (fh) fh.setup();

      // Reset toggles (checkboxes don't get reset by setup())
      document.querySelectorAll('.custom-toggle.active').forEach(t => t.classList.remove('active'));
      document.querySelectorAll('#native-filters input[type="checkbox"]').forEach(cb => {
        cb.checked = false;
        cb.dispatchEvent(new Event('change'));
      });

      // Reset insulation slider to default (position 11 = U-Wert 5.4 = Mono)
      if (insulationSlider) {
        insulationSlider.value = 11;
        updateZoneHighlight(11);
        updateThumbPosition(insulationSlider);
      }
    });
  }

  // Wizard button — re-open guided wizard
  const wizardBtn = document.getElementById('wizard-btn');
  if (wizardBtn) {
    wizardBtn.addEventListener('click', () => {
      if (window._cdrWizard) {
        localStorage.removeItem('cdr-wizard-v2');
        window._cdrWizard.show();
      }
    });
  }

  // File upload — show selected filename
  const fileInput = document.getElementById('form-dokument');
  const fileNameDisplay = document.getElementById('upload-filename');
  if (fileInput && fileNameDisplay) {
    fileInput.addEventListener('change', () => {
      fileNameDisplay.textContent = fileInput.files.length
        ? fileInput.files[0].name
        : 'Keine Datei gewählt';
    });
  }

  // Anfrage form — collapsible toggle
  const anfrageToggle = document.getElementById('anfrage-toggle');
  const anfrageBody = document.getElementById('anfrage-body');
  const anfrageChevron = document.getElementById('anfrage-chevron');
  if (anfrageToggle && anfrageBody) {
    anfrageToggle.addEventListener('click', () => {
      anfrageBody.classList.toggle('collapsed');
      if (anfrageChevron) {
        anfrageChevron.style.transform = anfrageBody.classList.contains('collapsed') ? '' : 'rotate(180deg)';
      }
    });
  }

  // Hero thickness modal (height-dependent glass thickness warning)
  new HeroThicknessModal(bridge);

  // Application mode → shadow floor toggle
  const APP_MODE_MAP = {
    'Inside': 'inside',
    'Inside/Outside': 'mixed',
    'Outside/Outside': 'outside'
  };

  // Resistance → subtitle mapping
  const RESISTANCE_SUBTITLE = {
    '-': 'entspricht RC0',
    'RC 2 (WK 2) / P4A': 'entspricht RC2',
    'RC 3 (WK 3) / P5A': 'entspricht RC3',
    'RC 4 (WK 4) / P6B': 'entspricht RC4'
  };
  const resistanceSubtitle = document.getElementById('resistance-subtitle');

  bridge.onChange((filterName, value) => {
    // Shadow floor
    if (filterName === 'application') {
      const mode = APP_MODE_MAP[value];
      if (mode && window._cdrSetApplication) {
        window._cdrSetApplication(mode);
      }
    }
    // Resistance subtitle
    if (filterName === 'resistance' && resistanceSubtitle) {
      resistanceSubtitle.textContent = RESISTANCE_SUBTITLE[value] || 'entspricht —';
    }
    // Sync zone labels + thumb position when insulation changes via cascade
    if (filterName === 'insulation' && insulationSlider) {
      const idx = parseInt(insulationSlider.value);
      updateZoneHighlight(idx);
      updateThumbPosition(insulationSlider);
    }
  });

  // Spacer slider + Gas segment controls (Anpassen column)
  initSpacerGasControls();

  // Collapsible Anpassen section
  initCollapsibleSections();

  // Wizard button — already created in onReady(); bridge is now connected

  // Initial sync: after data loads and filters set up, force V2 UI to match
  setTimeout(() => {
    if (insulationSlider) {
      updateZoneHighlight(parseInt(insulationSlider.value));
      updateThumbPosition(insulationSlider);
    }
  }, 300);

  // Entrance animations
  AnimationHelper.staggerEnter('.property-card', 60);
  AnimationHelper.fadeIn(document.querySelector('.glass-structure-card'), 400);
  AnimationHelper.fadeIn(document.querySelector('.dimensions-card'), 500);

  // Mobile bottom sheet behavior
  initMobileBottomSheet();

  // Mobile double-tap zoom toggle on canvas
  initDoubleTapZoom();

  // Desktop: orders tray starts collapsed — user opens via truck icon or header click

  // "+" FAB — opens Anpassen tab (mobile) or clicks add-to-order (desktop)
  const addFab = document.getElementById('add-position-fab');
  if (addFab) {
    addFab.addEventListener('click', () => {
      if (window.innerWidth <= 768) {
        // Mobile: expand sheet to settings tab
        if (window._expandBottomSheet) window._expandBottomSheet();
      } else {
        // Desktop: trigger the add-to-order button
        const addBtn = document.getElementById('add-to-order');
        if (addBtn) addBtn.click();
      }
    });
  }

  // Mobile top-right buttons: wire to same actions as desktop
  const mobileFaqToggle = document.getElementById('mobile-faq-toggle');
  if (mobileFaqToggle) {
    mobileFaqToggle.addEventListener('click', () => {
      const desktopFaq = document.getElementById('faq-toggle');
      if (desktopFaq) desktopFaq.click();
    });
  }
  // Mobile wizard toggle
  const mobileWizardToggle = document.getElementById('mobile-wizard-toggle');
  if (mobileWizardToggle) {
    mobileWizardToggle.addEventListener('click', () => {
      if (window._cdrWizard) {
        localStorage.removeItem('cdr-wizard-v2');
        window._cdrWizard.show();
      }
    });
  }

  const mobileCartToggle = document.getElementById('mobile-cart-toggle');
  if (mobileCartToggle) {
    mobileCartToggle.addEventListener('click', () => {
      if (window._switchToOrdersTab) window._switchToOrdersTab();
      if (window._expandBottomSheet) window._expandBottomSheet();
    });
  }

  // Sync mobile cart badge with desktop cart badge
  const desktopBadge = document.getElementById('cart-badge');
  const mobileBadge = document.getElementById('mobile-cart-badge');
  if (desktopBadge && mobileBadge) {
    const syncBadge = () => {
      mobileBadge.textContent = desktopBadge.textContent;
      mobileBadge.classList.toggle('hidden', desktopBadge.classList.contains('hidden'));
    };
    new MutationObserver(syncBadge).observe(desktopBadge, {
      childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['class']
    });
    syncBadge();
  }

  // --- Services ---
  const firebaseService = new FirebaseService();
  const emailService = new EmailService();
  const pdfExporter = new PDFExporter();
  window._cdrFirebase = firebaseService;

  // --- Authentication ---
  const auth = new AuthManager(firebaseService);
  window._cdrAuth = auth;

  // Dashboard (requires auth + firebase)
  const dashboard = new DashboardManager(auth, firebaseService);
  auth.setDashboard(dashboard);

  // Auth gating: pricing blur + order submission
  auth.onAuthChange((isLoggedIn) => {
    document.querySelectorAll('.stat-blurred').forEach(el => {
      if (isLoggedIn) el.classList.remove('stat-blurred');
      else el.classList.add('stat-blurred');
    });

    const submitBtn = document.getElementById('submit-order');
    if (submitBtn) {
      submitBtn.disabled = !isLoggedIn;
      submitBtn.title = isLoggedIn ? '' : 'Anmelden erforderlich';
    }
  });

  // Click blurred price to open login
  document.querySelectorAll('.tech-property-value').forEach(el => {
    el.addEventListener('click', () => {
      if (el.classList.contains('stat-blurred') && !auth.isLoggedIn()) {
        auth.openLoginModal();
      }
    });
  });

  // Submit order button: gate behind login, then submit to Firestore + email
  const submitBtn = document.getElementById('submit-order');
  if (submitBtn) {
    submitBtn.addEventListener('click', async (e) => {
      if (!auth.isLoggedIn()) {
        e.stopImmediatePropagation();
        auth.openLoginModal();
        return;
      }

      // Get positions from localStorage
      let orders = [];
      try {
        orders = JSON.parse(localStorage.getItem('cdr-configurator-orders') || '[]');
      } catch (err) { /* empty */ }

      if (orders.length === 0) {
        showToast('Bitte fügen Sie zuerst Positionen hinzu.', 'warning');
        return;
      }

      // Collect form data
      const formData = {
        kommission: document.getElementById('form-kommission')?.value || '',
        notiz: document.getElementById('form-notiz')?.value || '',
        offerteBis: document.getElementById('form-offerte-bis')?.value || '',
        lieferungBis: document.getElementById('form-lieferung-bis')?.value || '',
      };

      const user = auth.getUser();

      // Disable button, show spinner
      const btnText = submitBtn.querySelector('span');
      const origText = btnText?.textContent;
      if (btnText) btnText.textContent = ' Wird gesendet...';
      submitBtn.disabled = true;

      try {
        // 1. Save to Firestore
        const result = await firebaseService.submitOrder({ positions: orders, user, formData });

        // 2. Send email notifications
        const totalSqm = orders.reduce((sum, pos) => {
          return sum + (pos.variants || []).reduce((s, v) => {
            const sqm = (parseFloat(v.width) || 0) * (parseFloat(v.height) || 0) / 1000000;
            return s + sqm * (parseInt(v.quantity) || 1);
          }, 0);
        }, 0);

        await emailService.sendOrderEmails({
          code: result.code,
          user,
          positions: orders,
          formData,
          totalSqm,
        });

        showToast(`Anfrage ${result.code} erfolgreich gesendet!`, 'success');
      } catch (err) {
        console.error('Order submission failed:', err);
        showToast('Fehler beim Senden. Bitte versuchen Sie es erneut.', 'error');
      } finally {
        if (btnText) btnText.textContent = origText;
        submitBtn.disabled = false;
      }
    });
  }

  // PDF export button
  const pdfBtn = document.getElementById('export-pdf');
  if (pdfBtn) {
    pdfBtn.addEventListener('click', () => {
      let orders = [];
      try {
        orders = JSON.parse(localStorage.getItem('cdr-configurator-orders') || '[]');
      } catch (err) { /* empty */ }

      if (orders.length === 0) {
        showToast('Keine Positionen zum Exportieren.', 'warning');
        return;
      }

      const user = auth.isLoggedIn() ? auth.getUser() : { name: '—', email: '—', company: '—' };
      const formData = {
        kommission: document.getElementById('form-kommission')?.value || '',
        notiz: document.getElementById('form-notiz')?.value || '',
        offerteBis: document.getElementById('form-offerte-bis')?.value || '',
        lieferungBis: document.getElementById('form-lieferung-bis')?.value || '',
      };

      pdfExporter.generate({ positions: orders, user, formData });
      showToast('PDF wird heruntergeladen...', 'success');
    });
  }

  // Auth toggle buttons (desktop + mobile)
  document.querySelectorAll('#auth-toggle, #mobile-auth-toggle').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (auth.isLoggedIn()) {
        auth.toggleDropdown();
      } else {
        auth.openLoginModal();
      }
    });
  });

  // --- Column focus toggle (config mode ↔ order mode) ---
  const grid = document.querySelector('.configurator-grid');
  const colLeft = document.querySelector('.col-left');
  const cartHeader = document.querySelector('.cart-header');

  function setOrderFocus(on) {
    if (!grid) return;
    if (on) grid.classList.add('order-focus');
    else grid.classList.remove('order-focus');
    if (window.ViewApi) window.ViewApi.wake(500);
  }

  // Expose globally for OrderManager
  window._setOrderFocus = setOrderFocus;

  if (grid && colLeft) {
    // Click compact left column → expand it (switch back to config mode)
    colLeft.addEventListener('click', () => {
      if (grid.classList.contains('order-focus')) {
        setOrderFocus(false);
      }
    });

    // Click cart header → switch to order mode (expand right column)
    if (cartHeader) {
      cartHeader.style.cursor = 'pointer';
      cartHeader.addEventListener('click', () => {
        if (!grid.classList.contains('order-focus')) {
          setOrderFocus(true);
        }
      });
    }
  }

  console.log('V2 UI initialized');
}

function initSoundCardSync() {
  const soundCardValue = document.getElementById('sound-card-value');
  if (!soundCardValue) return;

  // Observe the hidden .sound element (populated by the filter system)
  const soundEl = document.querySelector('.sound');
  if (soundEl) {
    const update = () => {
      const text = soundEl.textContent || soundEl.innerHTML;
      soundCardValue.textContent = text || '— dB';
    };
    const observer = new MutationObserver(update);
    observer.observe(soundEl, { childList: true, characterData: true, subtree: true });
    update(); // initial sync
  }
}

function initSpacerGasControls() {
  const SPACER_VALUES = [0, 8, 10, 12, 14, 16, 18, 20];
  const spacerSlider = document.getElementById('spacer-slider');
  const spacerDisplay = document.getElementById('spacer-display');
  const gasSegment = document.getElementById('gas-segment');
  const gasDisplay = document.getElementById('gas-display');

  // Track last auto-synced values to detect user overrides
  let lastSyncedSpacer = -1;
  let lastSyncedGas = '0'; // default: "—" button is active

  // --- Spacer slider interaction ---
  if (spacerSlider) {
    spacerSlider.addEventListener('input', () => {
      const thickness = SPACER_VALUES[parseInt(spacerSlider.value)];
      if (spacerDisplay) {
        spacerDisplay.textContent = thickness ? thickness + ' mm' : '—';
      }
      const structure = window._cdrStructure;
      if (structure) {
        structure.spacerOutside.thickness = thickness;
        // Set both spacers (for 3-glazed ISO configurations)
        structure.spacerMiddle.thickness = thickness;
        structure.calculate_width();
      }
    });
  }

  // --- Gas segment interaction ---
  if (gasSegment) {
    gasSegment.querySelectorAll('.segment-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        gasSegment.querySelectorAll('.segment-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const val = btn.dataset.value;
        const gasName = val === '0' ? 0 : val;
        if (gasDisplay) {
          gasDisplay.textContent = gasName || '—';
        }
        const structure = window._cdrStructure;
        if (structure) {
          structure.gasOutside = gasName;
          // Set both gas fills (for 3-glazed ISO configurations)
          structure.gasMiddle = gasName;
          structure.calculate_width();
        }
      });
    });
  }

  // --- Auto-sync controls from GlassStructure (driven by insulation filter) ---
  setInterval(() => {
    const structure = window._cdrStructure;
    if (!structure) return;

    // Sync spacer slider position
    const s1 = structure.spacerOutside?.thickness || 0;
    if (s1 !== lastSyncedSpacer) {
      lastSyncedSpacer = s1;
      const idx = SPACER_VALUES.indexOf(s1);
      if (spacerSlider && idx !== -1) {
        spacerSlider.value = idx;
      }
      if (spacerDisplay) {
        spacerDisplay.textContent = s1 ? s1 + ' mm' : '—';
      }
    }

    // Sync gas segment active state
    const g1 = structure.gasOutside;
    const gasStr = g1 === 0 ? '0' : String(g1 || '0');
    if (gasStr !== lastSyncedGas) {
      lastSyncedGas = gasStr;
      if (gasSegment) {
        gasSegment.querySelectorAll('.segment-btn').forEach(btn => {
          btn.classList.toggle('active', btn.dataset.value === gasStr);
        });
      }
      if (gasDisplay) {
        gasDisplay.textContent = g1 && g1 !== 0 ? String(g1) : '—';
      }
    }
  }, 250);
}

function initDoubleTapZoom() {
  const canvas = document.querySelector('.canvas-wrapper');
  if (!canvas) return;

  const ZOOM_DEFAULT = 9.5;
  const ZOOM_CLOSE = 6;
  let zoomed = false;
  let lastTap = 0;

  canvas.addEventListener('touchend', (e) => {
    if (window.innerWidth > 768) return;
    if (e.touches.length > 0) return; // multi-touch, ignore

    const now = Date.now();
    if (now - lastTap < 300) {
      // Double tap detected
      e.preventDefault();
      const cam = window._cdrCameraController;
      if (!cam) return;

      zoomed = !zoomed;
      cam.reference_zoom = zoomed ? ZOOM_CLOSE : ZOOM_DEFAULT;
    }
    lastTap = now;
  });
}

function initCollapsibleSections() {
  const expandBtn = document.getElementById('anpassen-expand');
  const body = document.getElementById('anpassen-body');
  if (!expandBtn || !body) return;

  // Start collapsed — user clicks "Anpassen" button to expand
  body.classList.add('collapsed');

  expandBtn.addEventListener('click', () => {
    body.classList.toggle('collapsed');
    const isExpanded = !body.classList.contains('collapsed');
    const label = expandBtn.querySelector('span');
    if (label) label.textContent = isExpanded ? 'Schliessen' : 'Anpassen';
  });
}

function initMobileBottomSheet() {
  if (window.innerWidth > 768) return;

  const sheet = document.getElementById('mobile-bottom-sheet');
  if (!sheet) return;

  const handle = document.getElementById('sheet-handle');
  const content = document.getElementById('sheet-content');
  const colLeft = document.querySelector('.col-left');
  const colRight = document.querySelector('.col-right');
  const colCenter = document.querySelector('.col-center');
  const tabSettings = document.getElementById('sheet-tab-settings');
  const tabPositions = document.getElementById('sheet-tab-positions');

  if (!content || !colLeft || !colRight) return;

  // Create slider wrapper and move panels into it
  const slider = document.createElement('div');
  slider.className = 'sheet-slider';
  slider.appendChild(colLeft);
  slider.appendChild(colRight);
  content.appendChild(slider);

  // Default active tab
  content.setAttribute('data-active', 'settings');

  // Sheet states: 'peek' (handle+tabs only), 'mid' (66vh), 'full' (100vh)
  let state = 'peek';

  function setState(newState) {
    sheet.classList.remove('sheet-mid', 'sheet-full', 'sheet-dragging');
    sheet.style.transform = '';
    if (newState === 'mid') sheet.classList.add('sheet-mid');
    if (newState === 'full') sheet.classList.add('sheet-full');
    state = newState;

    // Resize canvas container to match visible area above the sheet
    // The Three.js renderer auto-detects canvas size changes each frame
    if (colCenter) {
      colCenter.classList.remove('canvas-mid', 'canvas-full');
      if (newState === 'mid') colCenter.classList.add('canvas-mid');
      else if (newState === 'full') colCenter.classList.add('canvas-full');
    }
    if (window._cdrToggleLabels) {
      window._cdrToggleLabels(newState === 'peek');
    }
    setTimeout(() => window.dispatchEvent(new Event('resize')), 380);
  }

  // --- Tab switching ---
  let activeTab = 'settings';
  function switchTab(tab) {
    activeTab = tab;
    tabSettings.classList.toggle('active', tab === 'settings');
    tabPositions.classList.toggle('active', tab === 'positions');
    slider.classList.remove('no-transition');
    slider.style.transform = '';
    content.setAttribute('data-active', tab);

    // Expand to mid if currently at peek
    if (state === 'peek') setState('mid');

    // Scroll the active panel to top
    const panel = tab === 'settings' ? colLeft : colRight;
    panel.scrollTo({ top: 0, behavior: 'instant' });
  }

  tabSettings.addEventListener('click', () => switchTab('settings'));
  tabPositions.addEventListener('click', () => switchTab('positions'));

  // --- Drag handle interaction (height-based) ---
  let isDragging = false;
  let dragStartY = 0;
  let dragStartHeight = 0;
  let currentHeight = 72;
  let lastY = 0;
  let lastTime = 0;
  let velocity = 0;

  function getHeightForState(s) {
    const h = window.innerHeight;
    if (s === 'peek') return 72;
    if (s === 'mid') return h * 0.66;
    return h;
  }

  function onDragStart(y) {
    isDragging = true;
    dragStartY = y;
    dragStartHeight = getHeightForState(state);
    currentHeight = dragStartHeight;
    lastY = y;
    lastTime = Date.now();
    velocity = 0;
    sheet.classList.add('sheet-dragging');
    sheet.classList.remove('sheet-mid', 'sheet-full');
    sheet.style.height = dragStartHeight + 'px';
  }

  function onDragMove(y) {
    if (!isDragging) return;
    const now = Date.now();
    const dt = now - lastTime;
    if (dt > 0) velocity = (y - lastY) / dt;
    lastY = y;
    lastTime = now;

    // Dragging up (negative dy) increases height
    const dy = y - dragStartY;
    currentHeight = Math.max(72, Math.min(dragStartHeight - dy, window.innerHeight));
    sheet.style.height = currentHeight + 'px';
  }

  function onDragEnd() {
    if (!isDragging) return;
    isDragging = false;
    sheet.classList.remove('sheet-dragging');
    sheet.style.height = '';

    const h = window.innerHeight;
    const peekH = 72;
    const midH = h * 0.66;
    const fullH = h;
    const fastSwipe = Math.abs(velocity) > 0.4;

    if (fastSwipe) {
      if (velocity > 0) {
        // Swiping down → shrink
        setState(state === 'full' ? 'mid' : 'peek');
      } else {
        // Swiping up → grow
        setState(state === 'peek' ? 'mid' : 'full');
      }
    } else {
      // Snap to nearest state by height
      const distances = [
        { s: 'full', d: Math.abs(currentHeight - fullH) },
        { s: 'mid', d: Math.abs(currentHeight - midH) },
        { s: 'peek', d: Math.abs(currentHeight - peekH) },
      ];
      distances.sort((a, b) => a.d - b.d);
      setState(distances[0].s);
    }
  }

  // Drag zone: handle + tabs area (the entire visible peek bar)
  const sheetTabs = sheet.querySelector('.sheet-tabs');
  const dragZones = [handle, sheetTabs].filter(Boolean);

  dragZones.forEach(zone => {
    zone.addEventListener('touchstart', (e) => {
      onDragStart(e.touches[0].clientY);
    }, { passive: true });

    zone.addEventListener('mousedown', (e) => {
      onDragStart(e.clientY);
      e.preventDefault();
    });
  });

  document.addEventListener('touchmove', (e) => {
    if (isDragging) onDragMove(e.touches[0].clientY);
  }, { passive: true });

  document.addEventListener('touchend', () => {
    if (isDragging) onDragEnd();
  }, { passive: true });

  document.addEventListener('mousemove', (e) => {
    if (isDragging) onDragMove(e.clientY);
  });
  document.addEventListener('mouseup', () => {
    if (isDragging) onDragEnd();
  });

  // --- Content gestures: interactive horizontal slide + pull-down-to-collapse ---
  let cStartX = 0, cStartY = 0, cStartScroll = 0;
  let cDirection = null; // 'horizontal' | 'vertical' | null
  let cTracking = false;
  let cVelocityX = 0, cLastX = 0, cLastTime = 0;

  function isSwipeExcluded(target) {
    return target.closest('.custom-slider') || target.closest('input') ||
           target.closest('select') || target.closest('textarea') ||
           target.closest('.segment-btn') || target.closest('button');
  }

  // Get the active panel's scroll top
  function activeScrollTop() {
    const panel = activeTab === 'settings' ? colLeft : colRight;
    return panel.scrollTop;
  }

  content.addEventListener('touchstart', (e) => {
    cStartX = e.touches[0].clientX;
    cStartY = e.touches[0].clientY;
    cStartScroll = activeScrollTop();
    cDirection = null;
    cTracking = !isSwipeExcluded(e.target);
    cVelocityX = 0;
    cLastX = cStartX;
    cLastTime = Date.now();
  }, { passive: true });

  content.addEventListener('touchmove', (e) => {
    if (!cTracking) return;

    const x = e.touches[0].clientX;
    const y = e.touches[0].clientY;
    const dx = x - cStartX;
    const dy = y - cStartY;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Lock direction after 10px
    if (!cDirection && (absDx > 10 || absDy > 10)) {
      cDirection = absDx > absDy ? 'horizontal' : 'vertical';
      if (cDirection === 'horizontal') {
        // Kill the CSS transition for live tracking
        slider.classList.add('no-transition');
      }
    }

    if (cDirection !== 'horizontal') return;

    // Track velocity
    const now = Date.now();
    const dt = now - cLastTime;
    if (dt > 0) cVelocityX = (x - cLastX) / dt;
    cLastX = x;
    cLastTime = now;

    // Compute slider offset — base position + drag delta
    const baseOffset = activeTab === 'positions' ? -50 : 0;
    // dx as percentage of container width (container = content width)
    const pct = (dx / content.offsetWidth) * 50;

    // Rubber-band: resist dragging past edges
    let offset = baseOffset + pct;
    if (offset > 0) offset = offset * 0.3; // resist past left edge
    if (offset < -50) offset = -50 + (offset + 50) * 0.3; // resist past right edge

    slider.style.transform = `translateX(${offset}%)`;
  }, { passive: true });

  content.addEventListener('touchend', (e) => {
    if (!cTracking) return;

    const endX = e.changedTouches[0].clientX;
    const endY = e.changedTouches[0].clientY;
    const dx = endX - cStartX;
    const dy = endY - cStartY;

    if (cDirection === 'horizontal') {
      // Re-enable CSS transition for snap
      slider.classList.remove('no-transition');
      slider.style.transform = '';

      const fastSwipe = Math.abs(cVelocityX) > 0.3;
      const movedEnough = Math.abs(dx) > content.offsetWidth * 0.2;

      if (fastSwipe || movedEnough) {
        const goingLeft = fastSwipe ? cVelocityX < 0 : dx < 0;
        if (goingLeft && activeTab === 'settings') switchTab('positions');
        else if (!goingLeft && activeTab === 'positions') switchTab('settings');
      }
      // If not enough movement, CSS snaps back to current tab position
      cTracking = false;
      return;
    }

    // Vertical pull-down: collapse sheet (when scrolled to top)
    if (cStartScroll <= 5 && dy > 60 && activeScrollTop() <= 0) {
      if (state === 'full') setState('mid');
      else if (state === 'mid') setState('peek');
    }
    cTracking = false;
  }, { passive: true });

  // --- Tap canvas to collapse ---
  if (colCenter) {
    colCenter.addEventListener('click', () => {
      if (window.innerWidth > 768) return;
      if (state !== 'peek') setState('peek');
    });
  }

  // --- Tap handle bar to toggle mid/peek ---
  handle.addEventListener('click', (e) => {
    if (isDragging) return;
    if (state === 'peek') setState('mid');
    else if (state === 'mid') setState('peek');
    else setState('mid');
  });

  // --- Global API ---
  window._switchToOrdersTab = () => {
    if (window.innerWidth <= 768) switchTab('positions');
  };

  window._switchToSettingsTab = () => {
    if (window.innerWidth <= 768) switchTab('settings');
  };

  window._collapseBottomSheet = () => {
    if (window.innerWidth <= 768) setState('peek');
  };

  window._isSheetExpanded = () => {
    return state !== 'peek';
  };

  window._expandBottomSheet = () => {
    if (window.innerWidth <= 768 && state === 'peek') setState('mid');
  };
}

// on ready call
if (document.readyState !== "loading") {
  onReady();
} else {
  document.addEventListener("DOMContentLoaded", onReady);
}
